/**
 * SpellV1 (P0-4b deliverable 3): the spell card of amendments 2.1 section 4,
 * stored in `rules_record.data` (kind spell). `text` is table-safe; `gmText`
 * is for the GM only. Frozen by 1.0b stage 2 (`DND5E_SCHEMA_STATUS`, `SPELL_SCHEMA_VERSION`):
 * from now on a change is additive only, through a contract steward round.
 */
import { Ability, CardSourceMeta, DamageType, RulesVersion, SpellSchool, Text } from './common.js';
import { z } from './zod.js';
export const CASTING_TIME_UNITS = [
    'action',
    'bonus',
    'reaction',
    'minute',
    'hour',
    'special',
];
export const CastingTime = z
    .strictObject({
    amount: z.number().int().min(1).nullable(),
    unit: z.enum(CASTING_TIME_UNITS),
    /** "which you take when you or a creature within 60 feet of you falls". */
    reactionTrigger: z.string().nullable(),
})
    .refine((c) => c.unit === 'reaction' || c.reactionTrigger === null, {
    message: 'Only a reaction has a trigger',
    path: ['reactionTrigger'],
});
export const RANGE_KINDS = ['self', 'touch', 'distance', 'sight', 'unlimited', 'special'];
export const DISTANCE_UNITS = ['ft', 'mi'];
/** dnd5e 5.x's area template types. */
export const AREA_SHAPES = [
    'cone',
    'cube',
    'cylinder',
    'line',
    'radius',
    'sphere',
    'square',
    'wall',
];
export const SpellRange = z
    .strictObject({
    kind: z.enum(RANGE_KINDS),
    distance: z.number().min(0).nullable(),
    unit: z.enum(DISTANCE_UNITS).nullable(),
    /** "Self (15-foot cone)". */
    shape: z
        .strictObject({
        type: z.enum(AREA_SHAPES),
        size: z.number().min(0),
        unit: z.enum(DISTANCE_UNITS).nullable(),
    })
        .nullable(),
})
    .refine((r) => (r.kind === 'distance') === (r.distance !== null), {
    message: 'A distance range, and only one, has a distance',
    path: ['distance'],
});
export const Components = z.strictObject({
    verbal: z.boolean(),
    somatic: z.boolean(),
    material: z.boolean(),
    /** "a drop of water", as printed. */
    materialText: z.string().nullable(),
    /** In gp, where the material has a cost. */
    materialCost: z.number().min(0).nullable(),
    materialConsumed: z.boolean(),
});
/** dnd5e 5.x's duration units. */
export const DURATION_UNITS = [
    'inst',
    'turn',
    'round',
    'minute',
    'hour',
    'day',
    'month',
    'year',
    'disp',
    'dstr',
    'perm',
    'spec',
];
export const Duration = z.strictObject({
    amount: z.number().int().min(1).nullable(),
    unit: z.enum(DURATION_UNITS),
    /** Printed "Concentration, up to …". */
    concentration: z.boolean(),
});
export const TARGET_TYPES = ['creature', 'object', 'point', 'area'];
export const CASTING_OPTION_KINDS = ['default', 'ritual', 'slot', 'characterLevel'];
/** One of Open5e's SpellCastingOption rows: how the spell changes when upcast or cast at a level. */
export const CastingOption = z
    .strictObject({
    kind: z.enum(CASTING_OPTION_KINDS),
    /** The slot level or the character level; null for default and ritual. */
    level: z.number().int().min(1).max(20).nullable(),
    damageRoll: z.string().nullable(),
    targetCount: z.number().int().min(0).nullable(),
    duration: z.string().nullable(),
    range: z.number().min(0).nullable(),
    shapeSize: z.number().min(0).nullable(),
    concentration: z.boolean().nullable(),
    text: z.string().nullable(),
})
    .refine((o) => (o.kind === 'slot' || o.kind === 'characterLevel') === (o.level !== null), {
    message: 'A slot or character-level option, and only one, has a level',
    path: ['level'],
});
export const SpellV1 = z
    .strictObject({
    schemaVersion: z.literal(1),
    source: CardSourceMeta,
    rules: RulesVersion.nullable(),
    name: Text,
    /** 0 is a cantrip. */
    level: z.number().int().min(0).max(9),
    school: SpellSchool,
    ritual: z.boolean(),
    castingTime: CastingTime,
    range: SpellRange,
    components: Components,
    duration: Duration,
    /** Class keys: "wizard", "cleric". */
    classes: z.array(Text),
    attackRoll: z.boolean(),
    saveAbility: Ability.nullable(),
    damage: z.strictObject({
        roll: z.string().nullable(),
        types: z.array(DamageType),
    }),
    target: z.strictObject({
        type: z.enum(TARGET_TYPES).nullable(),
        count: z.number().int().min(0).nullable(),
    }),
    /** Table-safe CommonMark. */
    text: z.string(),
    /** "At Higher Levels". */
    higherLevels: z.string().nullable(),
    /** GM-only text, never rendered for the table. */
    gmText: z.string().nullable(),
    upcast: z.array(CastingOption),
})
    .refine((s) => s.level > 0 || s.upcast.every((o) => o.kind !== 'slot'), {
    message: 'A cantrip is not cast with a slot',
    path: ['upcast'],
});
//# sourceMappingURL=spell.js.map